import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_helpers";
import { getSidebarData } from "../../sidebar/SidebarData";

const generateFromPath = (pathname, permissions, generalSettings, taxonomy) => {
  const sidebarItems = getSidebarData(permissions, generalSettings, taxonomy);

  const pathToTitleMap = {};

  sidebarItems.forEach((item) => {
    pathToTitleMap[item.path] = item.title;

    item.subItems.forEach((subItem) => {
      pathToTitleMap[subItem.path] = subItem.name;
    });
  });

  const segments = pathname.split("/").filter(Boolean);
  const breadcrumbs = [{ title: "Home", path: "/dashboard", isHome: true }];

  let currentPath = "";
  segments.forEach((segment) => {
    if (/^\d+$/.test(segment) || /^[a-f0-9]{16,}$/i.test(segment)) return;

    currentPath += `/${segment}`;

    let title = pathToTitleMap[currentPath];

    if (!title) {
      title =
        segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
    }

    breadcrumbs.push({ title, path: currentPath });
  });

  if (breadcrumbs.length > 1) {
    breadcrumbs[breadcrumbs.length - 1].path = null;
  }

  return {
    breadcrumbs,
    title: breadcrumbs[breadcrumbs.length - 1]?.title || "Unknown",
  };
};

export function BreadCrumbs({
  items,
  useAutoGeneration = false,
  fallbackToPath = true,
}) {
  const location = useLocation();
  const { currentPermissions } = useSelector((state) => state.role);
  const { generalSettings } = useSelector((state) => state.settings);
  const { taxonomy } = useSelector((state) => state?.taxonomy);

  const generatedData = useMemo(() => {
    if (!useAutoGeneration && items?.length) {
      return { breadcrumbs: items, title: "" };
    }

    return fallbackToPath
      ? generateFromPath(
          location.pathname,
          currentPermissions,
          generalSettings,
          taxonomy
        )
      : {
          breadcrumbs: [{ title: "Home", path: "/dashboard", isHome: true }],
          title: "Unknown",
        };
  }, [
    location.pathname,
    items,
    useAutoGeneration,
    fallbackToPath,
    currentPermissions,
    generalSettings,
    taxonomy,
  ]);

  const breadcrumbItems = generatedData.breadcrumbs;
  if (!breadcrumbItems?.length) return null;

  return (
    <ul className="breadcrumb p-0 my-2 mx-0 ms-2">
      <li
        style={{ color: "#00555C" }}
        className="ps-2 d-flex align-items-center gap-1">
        <Link
          to="/dashboard"
          className="text-decoration-none d-flex align-items-center"
          style={{ color: "#00555C" }}>
          <SVG
            src={toAbsoluteUrl("media/svg/icons/Sidebar/dashboard.svg")}
            className="home-icon"
            style={{
              width: "18px",
              height: "18px",
              filter: "grayscale(1) brightness(0.4)",
            }}
            title="Home"
          />
        </Link>
        {breadcrumbItems.length > 1 && (
          <span className="text-muted user-select-none ms-1">
            <SVG
              src={toAbsoluteUrl("media/svg/icons/Sidebar/chevron-right.svg")}
              className="h-5 w-5"
              title="chevron-right"
            />
          </span>
        )}
      </li>

      {breadcrumbItems.slice(1).map((item, index) => {
        const isLast = index === breadcrumbItems.slice(1).length - 1;
        return (
          <li
            key={index}
            className="ps-2 d-flex align-items-center gap-1 font-size-sm text-capitalize">
            {item.path ? (
              <p style={{ color: "#667085" }} className="user-select-none mb-0">
                {item.title}
              </p>
            ) : (
              <span
                style={{ color: "#00555C" }}
                className={isLast ? "fw-medium user-select-none" : ""}>
                {item.title}
              </span>
            )}
            {!isLast && (
              <span className="text-muted user-select-none ms-1">
                <SVG
                  src={toAbsoluteUrl(
                    "media/svg/icons/Sidebar/chevron-right.svg"
                  )}
                  className="h-5 w-5"
                  title="chevron-right"
                />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
